import { GlobalUtils } from "@/app/home/utils/global.utils";
import { IPermission, IPermissionGroup, PermissionClaim } from "@/core/enums/permissions.enum";
import { useUpdate } from "@/core/hooks/use-update.hook";
import { AppRoleModel } from "@/core/models/app-role.model";
import { normalizeVietnameseString } from "@/core/pipes/norm-vn.pipe";
import AuthRepo from "@/core/repos/auth.repo";
import { AppRoleRepo } from "@/core/repos/tables/app-role.repo";
import { MODAL_THEME } from "@/core/themes/modal.theme";
import container from "@/lib/di/container";
import { Modal, Button, ConfigProvider, Checkbox, message, TreeDataNode, Tree, Input } from "antd";
import { Dispatch, SetStateAction, useState, useEffect, useCallback, Key } from "react";

const appRoleRepo = container.get(AppRoleRepo);
const authRepo = container.get(AuthRepo);

const COLUMNS_COUNT = 3;

interface ClaimTemplate {
    id: string;
    group: string;
    checked: boolean;
    name: string;
}

interface PermissionModalProps {
    isModalOpen: boolean,
    setIsModalOpen: Dispatch<SetStateAction<boolean>>,
    data: AppRoleModel,
    permissions: Key[]
}
export const PermissionModal: React.FC<PermissionModalProps> = ({isModalOpen, setIsModalOpen, data, permissions}) => {
    const claims = authRepo.useClaims();
    const userDetail = authRepo.useDetails();
    const [loading, setLoading] = useState<boolean>(false);
    const [currentPermissions, setCurrentPermissions] = useState<Key[]>(permissions);
    const [columnData, setColumnData] = useState<{column: IPermissionGroup[], nodes: TreeDataNode[]}[]>([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [permissionGroups, setPermissionGroups] = useState<IPermissionGroup[]>([]);
    const [allowedPermissions, setAllowedPermissions] = useState<Key[]>([]);

    useUpdate(()=>{
        generatePermissionTreeColumns();
    }, [searchQuery, permissionGroups])

    const transformClaimTemplatesToPermissionGroups = (templates: ClaimTemplate[]): IPermissionGroup[] => {
        const groupMap = new Map<string, IPermission[]>();
        
        templates.forEach(template => {
            if (!groupMap.has(template.group)) {
                groupMap.set(template.group, []);
            }
            groupMap.get(template.group)!.push({
                key: template.id,
                name: template.name
            });
        });

        return Array.from(groupMap.entries()).map(([groupName, permissions]) => ({
            name: groupName,
            permissions: permissions
        }));
    }

    const init = useCallback(async () => {
        try {
            const templates = await appRoleRepo.getClaimTemplates();
            const groups = transformClaimTemplatesToPermissionGroups(templates);
            setPermissionGroups(groups);
            
            // Calculate allowed permissions (all ids except "Admin")
            const allowed = templates
                .filter(t => t.id !== "Admin")
                .map(t => t.id);
            setAllowedPermissions(allowed);
        } catch (error) {
            console.error("Failed to fetch claim templates:", error);
            message.error("Không thể tải danh sách quyền");
        }
    }, []);

    const generatePermissionTreeColumns = () => {
        if (permissionGroups.length === 0) return;
        
        const groupMatch = (group: IPermissionGroup) => {
            const name = normalizeVietnameseString(group.name).toLowerCase();
            const query = normalizeVietnameseString(searchQuery).toLowerCase();
            if (name.includes(query)) return true;
            return false;
        }
        const permissionMatch = (group: IPermissionGroup,  permission: IPermission) => {
            const isGroupMatched = groupMatch(group);
            if (isGroupMatched) return true;
            const query = normalizeVietnameseString(searchQuery).toLowerCase();
            const name = normalizeVietnameseString(permission.name).toLowerCase();
            const key = normalizeVietnameseString(permission.key).toLowerCase();
            if (key.includes(query)) return true;
            if (name.includes(query)) return true;
            return false;
        }
        const filteredGroups = permissionGroups.map((group): IPermissionGroup => ({
            name: group.name,
            permissions: group.permissions.filter((permission) => permissionMatch(group, permission))
        })).filter(g => g.permissions.length > 0);

        const columns = GlobalUtils.divideArray(filteredGroups, COLUMNS_COUNT);
        
        const data = columns.map((column) => {
            return {
                column: column,
                nodes: column.map((group, index): TreeDataNode => ({
                    title: group.name,
                    key: index,
                    children: group.permissions.map((permission): TreeDataNode => ({
                        title: permission.name,
                        key: permission.key
                    }))
                }))
            }
        })
        setColumnData(data);
    }

    const getCheckedPermission = (key: Key[], column: IPermissionGroup[]) => {
        const columnKeys = GlobalUtils.flattenArray(column.map((g: IPermissionGroup) => g.permissions.map(p => p.key)));
        const result = key.filter(key => columnKeys.includes(key));
        return result;
    }

    const isEditAllowed = (claims: PermissionClaim[]) => {
        const isAdmin = userDetail?.userName.toLowerCase() === "admin";
        if (isAdmin) return true;
        const isAllowed = claims.find(claim => claim.id === "Role.SetClaim");
        if (isAllowed) return true;
        return false;
    }

    useEffect(()=>{init()}, [data, init]);

    const handleCancel = () => {
        setIsModalOpen(false);
    };
    const handleSave = async () => {
        setLoading(true);
        const result = await appRoleRepo.updateClaims(data, currentPermissions.filter(p => typeof p === "string") as string[]);
        if (result) {
            setIsModalOpen(false);
            message.success({
                content: "Đã cập nhật"
            })
        } else {
            message.error({
                content: "Cập nhật thất bại"
            })
        }
        setLoading(false);
    };
    const toggleAllPermissions = (checked: boolean) => {
        let _permissions: Key[] = [];
        if (checked) {
            _permissions = [...allowedPermissions];
        } 
        setCurrentPermissions(_permissions);
    }
    const onChecked = (checked: Key[], column: IPermissionGroup[]) => {
        let _permissions = [...currentPermissions];
        const columnKeys = GlobalUtils.flattenArray(column.map((g: IPermissionGroup) => g.permissions.map(p => p.key)));
        const columnPermissions = _permissions.filter(p => columnKeys.includes(p));
        const {added, deleted} = GlobalUtils.findChanges(columnPermissions, checked);
        _permissions = _permissions.filter((p) => !deleted.includes(p));
        _permissions.push(...added);
        setCurrentPermissions(_permissions);
    }
    const isAllSelected = (key: Key[]) => {
        return (GlobalUtils.arrayContains(key, allowedPermissions));
    }
    return <Modal open={isModalOpen} onCancel={handleCancel} width={1000} style={{top: 50}}
        footer={[
            <div key={1} className="w-full flex flex-row justify-end px-1.5">
               {isEditAllowed(claims)&&<Button key="submit" type="primary" loading={loading}
                    style={{ 
                        background: "var(--primary-color)", 
                        paddingRight: 48, 
                        paddingLeft: 48, 
                        fontSize: 18,
                        fontWeight: 600,
                        paddingTop: 18, 
                        paddingBottom: 18,
                        }}
                    onClick={handleSave}
                >
                    LƯU
                </Button>}
            </div>,
        ]}
        styles={{
            mask: {
                backdropFilter: 'blur(2px)',
                backgroundColor: 'var(--primary-mask)'
            }
        }}
    >
        <div className="flex flex-col justify-start items-start select-none">
            <div className="flex flex-row w-full items-start justify-start select-none">
                <p className="ml-3 text-2xl font-bold text-primary">GÁN NHÓM TÀI KHOẢN</p>
            </div>
            <div className="mt-4 mb-3 px-3 w-full">
                <ConfigProvider theme={MODAL_THEME}>
                <div className="flex flex-col w-full items-start justify-start my-2">
                    <Input className="mx-2 mb-4" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Tìm kiếm Quyền/Nhóm quyền" style={{width: "100%"}} ></Input>
                    <Checkbox className="px-2 pb-2" disabled={!isEditAllowed(claims)} checked={isAllSelected(currentPermissions)} onChange={e => toggleAllPermissions(e.target.checked)}>Tất cả</Checkbox>
                    <div className="grid grid-cols-3">
                        {columnData.map((col, index) => {
                            return <Tree
                                key={index}
                                className="mx-2"
                                disabled={!isEditAllowed(claims)}
                                checkable
                                checkedKeys={getCheckedPermission(currentPermissions, col.column)}
                                onCheck={(checked: any) => onChecked(checked, col.column)}
                                treeData={col.nodes}
                            />
                        })}
                    </div>
                </div>
                </ConfigProvider>
            </div>
        </div>
        <div className="mb-5 mx-2 border-t border-dashed border-gray-300"></div>
    </Modal>
} 